import { useSearchParams } from 'react-router-dom';
import { GetShiftsByEventCommandOutput, Shift } from '@amzn/red-velvet-api';

import { useAllPagesRedVelvetQuery } from '../allPagesRedVelvetQuery';
import { useSingletonRedVelvetQuery } from '../singleItemRedVelvetQuery';
import { usePaginatedRedVelvetQuery } from '../paginatedRedVelvetQuery';

import { getValueFromSearchParam } from '../../utils/url';

export function useShiftFromUrlParams(enabled = true) {
  const [searchParams] = useSearchParams();

  const eventId = getValueFromSearchParam(searchParams, 'eventId', '');
  const shiftId = getValueFromSearchParam(searchParams, 'shiftId', '');

  const { queryResult: eventQuery } = useSingletonRedVelvetQuery({
    enabled,
    redVelvetQueryKey: ['getEventById', { eventId }],
  });
  // Pulled from the golden path in the Shift Carousel:
  // https://code.amazon.com/packages/RedVelvetReactWebsite/blobs/e5f651f6267ba96b4ba7e30e5fc62b41908eebbb/--/src/components/better/shiftCarousel.tsx#L78
  const shiftsQuery = useAllPagesRedVelvetQuery({
    enabled,
    redVelvetQueryKey: ['getShiftsByEvent', { eventId }],
    converter: (output: GetShiftsByEventCommandOutput) => ({
      items: output.shifts || [],
      next: output.page,
    }),
  });

  // Ensure coordinator name is loaded before displaying the info
  const { queryResult: coordinatorQuery } = usePaginatedRedVelvetQuery({
    enabled: enabled && eventQuery.data !== undefined,
    redVelvetQueryKey: [
      'getPeople',
      {
        aliases: eventQuery.data?.ownerEmail?.substring(
          0,
          eventQuery.data?.ownerEmail?.indexOf('@') || 0
        ),
      },
    ],
  });

  const shifts = shiftsQuery.data || [];
  const shift = shifts.find((shift: Shift) => shift.shiftId === shiftId);

  return {
    eventId,
    shiftId,
    event: eventQuery.data,
    shift,
    shifts,
    coordinator: coordinatorQuery.data,
    isFetched:
      !enabled ||
      !eventId ||
      !shiftId ||
      (shiftsQuery.isFetched && eventQuery.isFetched && coordinatorQuery.isFetched),
    isError: shiftsQuery.isError || eventQuery.isError || coordinatorQuery.isError,
  };
}
